import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { emptySearchResult } from "@utils/Constant";
import { Pageable } from "@type/pagination/pagination.types";
import { UserItemResponseDto } from "@state/users/dto/response/user.item.response.dto";
import { useTranslation } from "react-i18next";
import { message, Table } from "antd";
import { requestGetUsers } from "@state/users/UserEffects";
import PageLayout from "@components/layouts/PageLayout";
import PageBody from "@components/bodies/PageBody";
import { SpinIcon } from "@components/icons/SpinIcon";
import { columns } from "@components/lists/UsersColumns";
import { RouteComponentProps, useHistory } from "react-router-dom";

const UsersScreen: FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(true);

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const [users, setUsers] = useState<Pageable<UserItemResponseDto>>(
    emptySearchResult
  );

  const handleChangeLimit = (current: number, newLimit: number): void => {
    setLimit(newLimit);
  };

  const fetchUsers = useCallback(() => {
    setDataHasLoaded(false);
    requestGetUsers({
      page: pageNumber - 1,
      limit,
    })
      .catch(() => {
        void message.error(t("users.list.messages.loading-error"));
      })
      .finally(() => {
        setDataHasLoaded(true);
      });
  }, [pageNumber, limit, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    return requestGetUsers.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setUsers(result.data);
      }
    });
  });

  return (
    <PageLayout>
      <>
        <PageBody>
          <div className="d-flex flex-column align-items-stretch flex-sm-row">
            <div className="w-100 d-flex justify-content-between mb-3 flex-column flex-sm-row">
              <h4 className="text-primary">{t("users.title")}</h4>
            </div>
          </div>
          <Table
            style={{ whiteSpace: "break-spaces" }}
            locale={{ emptyText: dataHasLoaded ? "" : " " }}
            loading={{ spinning: !dataHasLoaded, indicator: SpinIcon }}
            columns={columns()}
            dataSource={users.content}
            pagination={{
              total: users.totalElements,
              pageSize: users.size,
              current: users.number + 1,
              showSizeChanger: true,
              onChange: setPageNumber,
              onShowSizeChange: handleChangeLimit,
            }}
            showSorterTooltip={false}
            rowKey={(record) => record.id}
            rowClassName="cursor-pointer"
            onRow={(record) => {
              return {
                onClick: () => history.push(`/users/${record.id}`),
              };
            }}
          />
        </PageBody>
      </>
    </PageLayout>
  );
};

export default UsersScreen;
